/**
 * @voltxampere/engine — Assertion DSL token types (Sprint 9)
 *
 * The DSL lexer (lexer.ts) emits a stream of Tokens that the
 * recursive-descent parser (parser.ts) consumes. Tokens carry
 * their source position so the parser can produce i18n error
 * messages that point directly at the failing character.
 *
 * Blueprint §24.2 lists the grammar surface; SPECS §6 is the
 * formal EBNF. The kinds below are a superset covering every
 * terminal those grammars reference plus a dedicated TOLERANCE
 * token for the `±` sign so the parser does not need look-ahead
 * to tell `±` from unary minus.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/**
 * Canonical comparator symbols after normalisation. The parser
 * uses these as keys into operator tables; the evaluator uses
 * them as branches when checking passed/failed.
 */
export const COMPARATOR_SYMBOLS = ["≈", "==", "<", ">", "≤", "≥", "!="];
/**
 * ASCII → canonical comparator alternates. `==` has no Unicode
 * alternate because `=` is already the condition separator; a
 * lone `=` would be ambiguous so the lexer demands a doubled form
 * (`==`) when equality is intended.
 */
export const COMPARATOR_ASCII_MAP = new Map([
    ["~=", "≈"],
    ["<=", "≤"],
    [">=", "≥"],
    ["!=", "!="],
    ["==", "=="],
    ["<", "<"],
    [">", ">"],
]);
export const EN_KEYWORDS = {
    assertion: ["assert"],
    conditionAt: ["at"],
    conditionWhen: ["when"],
    simEqualsAlias: ["sim"],
    allTimes: ["all times"],
    oscillates: ["oscillates"],
    source: ["source"],
};
export const TR_KEYWORDS = {
    assertion: ["iddia"],
    conditionAt: ["zaman"],
    conditionWhen: ["ne zaman"],
    simEqualsAlias: ["sim"],
    allTimes: ["tüm zamanlar"],
    oscillates: ["döngü"],
    source: ["kaynak"],
};
export function keywordsFor(locale) {
    return locale === "tr" ? TR_KEYWORDS : EN_KEYWORDS;
}
/**
 * SI engineering-notation prefixes the lexer recognises on
 * numbers, mapped to their power-of-ten multiplier. `μ` is
 * included alongside its ASCII alternate `u`; the lexer
 * canonicalises to `μ`. The empty string is NOT a prefix —
 * bare units (`V`, `A`, `Hz`, `s`, `Ω`) do not pass through the
 * prefix table.
 */
export const SI_PREFIXES = new Map([
    ["G", 1e9],
    ["M", 1e6],
    ["k", 1e3],
    ["m", 1e-3],
    ["μ", 1e-6],
    ["u", 1e-6],
    ["n", 1e-9],
    ["p", 1e-12],
    ["f", 1e-15],
]);
/**
 * Base units the lexer recognises. Any letter run not starting
 * with a known SI prefix is matched against this set directly.
 * `Ohm` is the ASCII alternate for `Ω`; the lexer canonicalises
 * to `Ω`.
 */
export const BASE_UNITS = new Set([
    "V",
    "A",
    "Hz",
    "s",
    "W",
    "F",
    "H",
    "Ω",
    "Ohm",
]);
/**
 * Whether a character can start a bare identifier. DSL identifiers
 * allow ASCII letters, underscore, and Turkish accented letters so
 * a Turkish user writing `iddia V(NÖKTA) < 5V` does not need to
 * romanise their net names.
 */
export function isIdentifierStart(ch) {
    if (ch === "_")
        return true;
    if (ch >= "A" && ch <= "Z")
        return true;
    if (ch >= "a" && ch <= "z")
        return true;
    // Unicode SI micro symbol (U+00B5 / U+03BC) starts the `μF`,
    // `μA` etc. unit runs when the user types them directly.
    if (ch === "μ" || ch === "µ")
        return true;
    // Ω (U+03A9) is the impedance base-unit, reachable bare (`1Ω`)
    // or as the tail of `kΩ` / `MΩ`.
    if (ch === "Ω")
        return true;
    return /^[çğıöşüÇĞİÖŞÜ]$/.test(ch);
}
/** Whether a character can continue an identifier (adds digits). */
export function isIdentifierContinue(ch) {
    if (ch >= "0" && ch <= "9")
        return true;
    return isIdentifierStart(ch);
}
//# sourceMappingURL=tokens.js.map